import React, { useEffect, useMemo, useState } from 'react';
import { customersAPI, businessesAPI } from '../services/api';
import api from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import ErrorBoundary from '../components/ErrorBoundary';

const Customers = ({ selectedBusiness }) => {
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const defaultForm = useMemo(() => ({
    business_id: selectedBusiness || '',
    customer_type: 'individual',
    customer_name: '',
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    billing_address_line1: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_country: 'USA',
    payment_terms: 'Net 30',
    credit_limit: '',
    customer_status: 'active',
    customer_priority: 'normal',
    notes: ''
  }), [selectedBusiness]);

  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, business_id: selectedBusiness || '' }));
  }, [selectedBusiness]);

  useEffect(() => { loadData(); }, [selectedBusiness, typeFilter, statusFilter, priorityFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBusiness) params.business_id = selectedBusiness;
      if (typeFilter) params.customer_type = typeFilter;
      if (statusFilter) params.customer_status = statusFilter;
      if (priorityFilter) params.customer_priority = priorityFilter;
      if (search) params.search = search;
      const [custRes, sumRes, bizRes] = await Promise.all([
        customersAPI.getAll(params), customersAPI.getSummary(params), businessesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSummary(sumRes.data || null);
      setBusinesses(bizRes.data || []);
      setError('');
    } catch (err) {
      setError(formatApiError(err, 'Failed to load customers'));
    } finally { setLoading(false); }
  };

  const validateForm = (d) => {
    const errs = {};
    if (!d.customer_name?.trim()) errs.customer_name = 'Name is required';
    if (!d.customer_type) errs.customer_type = 'Type is required';
    if (d.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) errs.email = 'Invalid email format';
    return errs;
  };

  const resetForm = () => { setFormData(defaultForm); setEditingCustomer(null); setFormErrors({}); setFormError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm(formData);
    setFormErrors(errs);
    if (Object.keys(errs).length) { setFormError('Please fix the highlighted fields.'); return; }
    try {
      setSaving(true);
      if (editingCustomer) { await customersAPI.update(editingCustomer.id, formData); setToast('Customer updated'); }
      else { await customersAPI.create(formData); setToast('Customer created'); }
      setShowModal(false); resetForm(); loadData();
    } catch (err) { setFormError(formatApiError(err, 'Failed to save customer')); }
    finally { setSaving(false); setTimeout(() => setToast(''), 2500); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try { await customersAPI.delete(id); setToast('Customer deleted'); loadData(); }
    catch (err) { setError(formatApiError(err, 'Failed to delete customer')); }
    finally { setTimeout(() => setToast(''), 2500); }
  };

  const openCreate = () => { resetForm(); setShowModal(true); };
  const openEdit = (c) => {
    setEditingCustomer(c);
    setFormData({
      business_id: c.business_id || selectedBusiness || '',
      customer_type: c.customer_type || 'individual',
      customer_name: c.customer_name || '',
      company_name: c.company_name || '',
      contact_person: c.contact_person || '',
      email: c.email || '',
      phone: c.phone || '',
      billing_address_line1: c.billing_address_line1 || '',
      billing_city: c.billing_city || '',
      billing_state: c.billing_state || '',
      billing_zip: c.billing_zip || '',
      billing_country: c.billing_country || 'USA',
      payment_terms: c.payment_terms || 'Net 30',
      credit_limit: c.credit_limit || '',
      customer_status: c.customer_status || 'active',
      customer_priority: c.customer_priority || 'normal',
      notes: c.notes || ''
    });
    setShowModal(true);
  };

  const businessName = (id) => businesses.find(b => b.id === Number(id))?.name || '—';

  return (
    <ErrorBoundary section="Customers">
    <div className="space-y-4 lg:space-y-6">
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Customers</h2>
          <p className="text-sm lg:text-base text-gray-600 mt-1">Manage your customer relationships and CRM data</p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2">
          <input type="text" placeholder="Search name/email" value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=>{if(e.key==='Enter') loadData();}} className="border border-gray-300 rounded px-2 lg:px-3 py-2 text-sm lg:text-base flex-1 sm:flex-initial sm:w-56" />
          <select value={typeFilter} onChange={(e)=>setTypeFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-2 text-sm lg:text-base"><option value="">All Types</option><option value="individual">Individual</option><option value="business">Business</option></select>
          <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-2 text-sm lg:text-base"><option value="">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option></select>
          <select value={priorityFilter} onChange={(e)=>setPriorityFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-2 text-sm lg:text-base"><option value="">All Priority</option><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="vip">VIP</option></select>
          <button onClick={() => loadData()} className="px-3 py-2 border rounded hover:bg-gray-50 text-sm lg:text-base">Apply</button>
          <button onClick={openCreate} className="bg-blue-600 text-white px-3 lg:px-4 py-2 rounded hover:bg-blue-700 text-sm lg:text-base">+ Add Customer</button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <div className="bg-white rounded shadow p-3 lg:p-4"><div className="text-xs lg:text-sm text-gray-500">Total Customers</div><div className="text-lg lg:text-2xl font-semibold">{summary.total_customers || 0}</div></div>
          <div className="bg-white rounded shadow p-3 lg:p-4"><div className="text-xs lg:text-sm text-gray-500">Active</div><div className="text-lg lg:text-2xl font-semibold">{summary.active_customers || 0}</div></div>
          <div className="bg-white rounded shadow p-3 lg:p-4"><div className="text-xs lg:text-sm text-gray-500">Outstanding (AR)</div><div className="text-base lg:text-2xl font-semibold">${Number(summary.total_outstanding || 0).toFixed(2)}</div></div>
          <div className="bg-white rounded shadow p-3 lg:p-4"><div className="text-xs lg:text-sm text-gray-500">VIP Customers</div><div className="text-lg lg:text-2xl font-semibold">{summary.vip_customers || 0}</div></div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs lg:text-sm">
            <thead className="bg-gray-50"><tr>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700">Name</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700 hidden sm:table-cell">Type</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700 hidden md:table-cell">Contact</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700 hidden lg:table-cell">Invoices</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700 hidden lg:table-cell">Open</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700">Outstanding</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700 hidden sm:table-cell">Status</th>
              <th className="text-left px-2 lg:px-4 py-2 font-medium text-gray-700 hidden md:table-cell">Priority</th>
              <th className="px-2 lg:px-4 py-2" />
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="px-2 lg:px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan="9" className="px-2 lg:px-4 py-8 text-center text-gray-500">No customers found</td></tr>
              ) : customers.map((c) => (
                <tr key={c.id} className="border-t">
                  <td className="px-2 lg:px-4 py-2">
                    <div className="font-medium text-gray-900">{c.customer_name}</div>
                    {c.company_name && (<div className="text-gray-500 text-xs">{c.company_name}</div>)}
                    {c.business_id && (<div className="text-xs text-gray-400">{businessName(c.business_id)}</div>)}
                  </td>
                  <td className="px-2 lg:px-4 py-2 capitalize hidden sm:table-cell">{c.customer_type || 'individual'}</td>
                  <td className="px-2 lg:px-4 py-2 hidden md:table-cell"><div className="truncate max-w-[150px]">{c.email || '—'}</div><div className="text-gray-500">{c.phone || '—'}</div></td>
                  <td className="px-2 lg:px-4 py-2 hidden lg:table-cell">{c.invoice_count || 0}</td>
                  <td className="px-2 lg:px-4 py-2 hidden lg:table-cell">{c.open_invoices || 0}</td>
                  <td className="px-2 lg:px-4 py-2">${Number(c.current_balance || 0).toFixed(2)}</td>
                  <td className="px-2 lg:px-4 py-2 capitalize hidden sm:table-cell"><span className={`px-2 py-1 rounded text-xs ${c.customer_status === 'active' ? 'bg-green-50 text-green-700' : c.customer_status === 'suspended' ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{c.customer_status || 'active'}</span></td>
                  <td className="px-2 lg:px-4 py-2 capitalize hidden md:table-cell">{c.customer_priority || 'normal'}</td>
                  <td className="px-2 lg:px-4 py-2 text-right">
                    <button onClick={() => openEdit(c)} className="px-2 lg:px-3 py-1 border rounded hover:bg-gray-50 mr-1 lg:mr-2 text-xs lg:text-sm">Edit</button>
                    <button onClick={() => handleDelete(c.id)} className="px-2 lg:px-3 py-1 border border-red-300 text-red-600 rounded hover:bg-red-50 text-xs lg:text-sm">Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {error && (
          <div className="p-3 bg-red-50 border-t border-red-200 text-red-700 flex items-start justify-between" role="alert">
            <span>{error}</span>
            <button type="button" disabled={reporting} onClick={async ()=>{
              try { setReporting(true); await api.post('/support', { area: 'customers', message: error, url: window.location.href, path: window.location.pathname, payload: { selectedBusiness, filters: { typeFilter, statusFilter, priorityFilter, search } } }); alert('Thanks! Your report was sent.'); }
              catch { alert('Could not send report.'); }
              finally { setReporting(false); }
            }} className="ml-3 text-sm text-blue-700 hover:underline disabled:text-blue-300">Report issue</button>
          </div>
        )}
      </div>

      {toast && (<div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded shadow">{toast}</div>)}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-4 lg:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-base lg:text-lg font-semibold">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h3>
              <button onClick={()=>{ setShowModal(false); resetForm(); }} className="text-gray-500 text-xl">✕</button>
            </div>

            <button type="button" onClick={()=>setShowHelp(!showHelp)} className="mb-4 text-xs lg:text-sm text-blue-600 hover:underline">{showHelp ? 'Hide help' : 'Need help?'}</button>
            {showHelp && (
              <div className="mb-4 p-2 lg:p-3 rounded bg-gray-50 border border-gray-200 text-xs lg:text-sm text-gray-700">
                <p className="mb-1">- Name and Type are required.</p>
                <p className="mb-1">- Email format is validated if provided.</p>
                <p className="mb-1">- Business defaults from the selector at the top.</p>
                <p className="mb-1">- Payment terms default to Net 30; set credit limit if applicable.</p>
              </div>
            )}

            {formError && (
              <div className="mb-4 p-2 lg:p-3 rounded bg-red-50 border border-red-200 text-red-700 flex items-start justify-between text-xs lg:text-sm" role="alert">
                <span>{formError}</span>
                <button type="button" disabled={reporting} onClick={async()=>{
                  try { setReporting(true); await api.post('/support', { area: 'customers', message: formError, url: window.location.href, path: window.location.pathname, payload: { formData, editing: !!editingCustomer } }); alert('Thanks! Your report was sent.'); }
                  catch { alert('Could not send report.'); }
                  finally { setReporting(false); }
                }} className="ml-3 text-xs lg:text-sm text-blue-700 hover:underline disabled:text-blue-300">Report issue</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="business_id" value={selectedBusiness || editingCustomer?.business_id || ''} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Type *</label>
                  <select value={formData.customer_type} onChange={(e)=>setFormData({ ...formData, customer_type: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" required>
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                  </select>
                  {formErrors.customer_type && (<p className="mt-1 text-xs lg:text-sm text-red-600">{formErrors.customer_type}</p>)}
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Name *</label>
                  <input type="text" value={formData.customer_name} onChange={(e)=>setFormData({ ...formData, customer_name: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" required />
                  {formErrors.customer_name && (<p className="mt-1 text-xs lg:text-sm text-red-600">{formErrors.customer_name}</p>)}
                </div>
                {formData.customer_type === 'business' && (
                  <>
                    <div>
                      <label className="block text-xs lg:text-sm font-medium mb-1">Company Name</label>
                      <input type="text" value={formData.company_name} onChange={(e)=>setFormData({ ...formData, company_name: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                    </div>
                    <div>
                      <label className="block text-xs lg:text-sm font-medium mb-1">Contact Person</label>
                      <input type="text" value={formData.contact_person} onChange={(e)=>setFormData({ ...formData, contact_person: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Email</label>
                  <input type="email" value={formData.email} onChange={(e)=>setFormData({ ...formData, email: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                  {formErrors.email && (<p className="mt-1 text-xs lg:text-sm text-red-600">{formErrors.email}</p>)}
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Phone</label>
                  <input type="text" value={formData.phone} onChange={(e)=>setFormData({ ...formData, phone: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Billing Address Line 1</label>
                  <input type="text" value={formData.billing_address_line1} onChange={(e)=>setFormData({ ...formData, billing_address_line1: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">City</label>
                  <input type="text" value={formData.billing_city} onChange={(e)=>setFormData({ ...formData, billing_city: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">State</label>
                  <input type="text" value={formData.billing_state} onChange={(e)=>setFormData({ ...formData, billing_state: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">ZIP</label>
                  <input type="text" value={formData.billing_zip} onChange={(e)=>setFormData({ ...formData, billing_zip: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Country</label>
                  <input type="text" value={formData.billing_country} onChange={(e)=>setFormData({ ...formData, billing_country: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Payment Terms</label>
                  <input type="text" value={formData.payment_terms} onChange={(e)=>setFormData({ ...formData, payment_terms: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Credit Limit</label>
                  <input type="number" step="0.01" value={formData.credit_limit} onChange={(e)=>setFormData({ ...formData, credit_limit: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Status</label>
                  <select value={formData.customer_status} onChange={(e)=>setFormData({ ...formData, customer_status: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"><option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option></select>
                </div>
                <div>
                  <label className="block text-xs lg:text-sm font-medium mb-1">Priority</label>
                  <select value={formData.customer_priority} onChange={(e)=>setFormData({ ...formData, customer_priority: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="vip">VIP</option></select>
                </div>
              </div>

              <div>
                <label className="block text-xs lg:text-sm font-medium mb-1">Notes</label>
                <textarea rows="3" value={formData.notes} onChange={(e)=>setFormData({ ...formData, notes: e.target.value })} className="w-full border rounded px-2 lg:px-3 py-2 text-sm lg:text-base" />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <button type="button" onClick={()=>{ setShowModal(false); resetForm(); }} className="px-4 py-2 border rounded hover:bg-gray-50 text-sm lg:text-base">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 text-sm lg:text-base">{saving ? 'Saving…' : (editingCustomer ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default Customers;

